import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { TrackRow } from "@/components/TrackRow";
import { GenreColumns } from "@/components/GenreColumns";
import { FeaturedBeat } from "@/components/FeaturedBeat";
import { TrendingBeats } from "@/components/TrendingBeats";
import { BannerAd } from "@/components/BannerAd";
import { Footer } from "@/components/Footer";
import { AboutComann } from "@/components/AboutComann";
import { LiveSalesFeed } from "@/components/LiveSalesFeed";
import { NextDropTeaser } from "@/components/NextDropTeaser";
import { Disc3, ArrowDown, Loader2, Trophy, SlidersHorizontal } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1632582204758-5ac65783517a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxtdXNpYyUyMHByb2R1Y2VyJTIwc3R1ZGlvJTIwZGFyayUyMGxpZ2h0aW5nfGVufDB8fHx8MTc4MjcxMDQ3Nnww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [owned, setOwned] = useState(new Set());
  const [search, setSearch] = useState("");
  const [activeGenre, setActiveGenre] = useState("All");
  const [sortMode, setSortMode] = useState("default");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/tracks").then((r) => setTracks(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user) {
      api.get("/purchases").then((r) => setOwned(new Set(r.data.map((t) => t.id)))).catch(() => {});
    } else {
      setOwned(new Set());
    }
  }, [user]);

  const genres = useMemo(() => {
    const set = new Set();
    for (const t of tracks) {
      if (!t.sold) set.add(t.genre || "Other");
    }
    return ["All", ...Array.from(set).sort()];
  }, [tracks]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    let avail = tracks.filter((t) => !t.sold); // hide sold beats from the store
    if (activeGenre !== "All") {
      avail = avail.filter((t) => (t.genre || "Other") === activeGenre);
    }
    if (!q) return avail;
    return avail.filter(
      (t) =>
        t.title.toLowerCase().includes(q) ||
        t.producer.toLowerCase().includes(q) ||
        (t.genre || "").toLowerCase().includes(q)
    );
  }, [tracks, search, activeGenre]);

  const sorted = useMemo(() => {
    if (sortMode === "default") return filtered;
    const dir = sortMode === "bpm-asc" ? 1 : -1;
    return [...filtered].sort((a, b) => ((a.bpm || 0) - (b.bpm || 0)) * dir);
  }, [filtered, sortMode]);

  const featured = useMemo(() => tracks.find((t) => t.featured && !t.sold) || null, [tracks]);

  return (
    <div className="min-h-screen grain pb-32">
      <Navbar search={search} setSearch={setSearch} />

      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#2a2a2a]">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="studio" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
        </div>
        <div className="relative z-10 px-6 md:px-12 py-24 md:py-36 max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-xs font-mono text-[#e2ff31] mb-6 fade-up">
            <span className="flex items-end gap-[2px] h-3">
              <span className="eq-bar" style={{ animationDelay: "0s" }} />
              <span className="eq-bar" style={{ animationDelay: "0.2s" }} />
              <span className="eq-bar" style={{ animationDelay: "0.4s" }} />
            </span>
            NEW BEATS DROPPED WEEKLY
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-bold leading-[1.05] fade-up" style={{ animationDelay: "0.05s" }}>
            Find your next <span className="text-[#e2ff31]">hit</span> beat.
          </h1>
          <p className="mt-6 text-lg text-[#9ca3af] max-w-xl leading-relaxed fade-up" style={{ animationDelay: "0.1s" }}>
            Stream high-quality, studio-ready beats and own them instantly. One click to preview, one click to buy and download.
          </p>
          <div className="mt-8 flex items-center gap-4 fade-up" style={{ animationDelay: "0.15s" }}>
            <a href="#tracks" data-testid="hero-browse-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#e2ff31] text-black hover:bg-[#c8e619] active:scale-95 transition-all font-semibold px-7 py-3.5">
              Browse beats <ArrowDown className="w-4 h-4" />
            </a>
            <span className="text-sm text-[#6b7280] font-mono">{filtered.length} tracks available</span>
          </div>
        </div>
      </section>

      {!search && <NextDropTeaser />}

      {/* Featured beat headlines the homepage */}
      {!search && <FeaturedBeat track={featured} owned={owned.has(featured?.id)} />}

      {/* Trending (driven by play counts) */}
      {!search && <TrendingBeats tracks={filtered} owned={owned} />}

      {/* Self-served banner ad campaigns (managed in the admin Monetization tab) */}
      <BannerAd />
      <section id="tracks" className="px-6 md:px-12 py-12 relative z-10">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">The Catalog</h2>
            <p className="text-[#9ca3af] mt-1">Organized by genre. Preview any beat — buy the ones that move you.</p>
          </div>
          <Link to="/sold" data-testid="catalog-sold-link"
            className="hidden sm:inline-flex items-center gap-2 text-sm text-[#9ca3af] hover:text-[#e2ff31] transition-colors shrink-0">
            <Trophy className="w-4 h-4" /> Sold Vault
          </Link>
        </div>

        {/* Genre filter chips + tempo sort */}
        {(genres.length > 1 || sorted.length > 0) && (
          <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
            <div data-testid="genre-filter-bar" className="flex flex-wrap gap-2">
              {genres.map((g) => {
                const isActive = activeGenre === g;
                return (
                  <button
                    key={g}
                    data-testid={`genre-chip-${g}`}
                    onClick={() => setActiveGenre(g)}
                    className={`rounded-full px-4 py-1.5 text-sm font-medium border transition-all active:scale-95 ${
                      isActive
                        ? "bg-[#e2ff31] text-black border-[#e2ff31]"
                        : "bg-transparent text-[#9ca3af] border-[#2a2a2a] hover:border-[#e2ff31]/50 hover:text-white"
                    }`}
                  >
                    {g}
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-1.5 shrink-0" data-testid="bpm-sort">
              <span className="text-xs font-mono text-[#6b7280] flex items-center gap-1 mr-1">
                <SlidersHorizontal className="w-3.5 h-3.5" /> Tempo
              </span>
              {[
                { key: "default", label: "Default" },
                { key: "bpm-asc", label: "Slow → Fast" },
                { key: "bpm-desc", label: "Fast → Slow" },
              ].map((opt) => (
                <button
                  key={opt.key}
                  data-testid={`sort-${opt.key}`}
                  onClick={() => setSortMode(opt.key)}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-all active:scale-95 ${
                    sortMode === opt.key
                      ? "bg-[#e2ff31] text-black border-[#e2ff31]"
                      : "bg-transparent text-[#9ca3af] border-[#2a2a2a] hover:border-[#e2ff31]/50 hover:text-white"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#e2ff31]" /></div>
        ) : sorted.length === 0 ? (
          <div data-testid="empty-tracks" className="text-center py-24 border border-dashed border-[#2a2a2a] rounded-2xl">
            <Disc3 className="w-12 h-12 text-[#2a2a2a] mx-auto mb-4" />
            <p className="text-[#9ca3af]">{search || activeGenre !== "All" ? "No beats match your filters." : "No beats uploaded yet. Check back soon!"}</p>
          </div>
        ) : (
          <GenreColumns tracks={sorted} owned={owned} preserveOrder={sortMode !== "default"} />
        )}
      </section>

      <AboutComann />

      <Footer />
      <LiveSalesFeed />
    </div>
  );
}
